/**
 * TOUR-03 lot 5 — export / import d'un scénario (R-TOUR-03-7, B-4 / B-5). Aucune dépendance React.
 *  - GET  /simulations/{id}/export  → JSON autoportant, téléchargé tel quel (409 tant que le calcul n'est pas terminé)
 *  - POST /simulations/import       → multipart `file` ; 422 patrimoine inconnu / JSON invalide ; crée un scénario enregistré
 * Un scénario importé porte `parametres.importe = {de, exporteLe, solveur}`.
 */

export const TAILLE_MAX_IMPORT = 5 * 1024 * 1024;

// Caractères interdits d'un nom de fichier (Windows) et caractères de contrôle.
const INTERDITS = new RegExp('[\\\\/:*?"<>|\\u0000-\\u001f]', 'g');

/** B-4 — `simulation-12-Cognac test.json` : caractères interdits retirés, libellé tronqué à 80. */
export function nomFichierExport(simulation) {
    const libelle = String(simulation?.libelle ?? '').replace(INTERDITS, '').replace(/\s+/g, ' ').trim().slice(0, 80).trim();
    return `simulation-${simulation?.id}${libelle ? `-${libelle}` : ''}.json`;
}

/** L'export n'est proposé que sur un scénario terminé. */
export const peutExporter = (simulation) => simulation?.statut === 'done';

/**
 * Corps téléchargé : la réponse sans les clés JSON-LD (`@context`, `@id`, `@type`) que l'en-tête Accept de la slice ajoute,
 * indentée pour être relue. Un corps illisible est rendu tel quel.
 */
export function contenuExport(texte) {
    try {
        const corps = JSON.parse(texte);
        if (corps && typeof corps === 'object' && !Array.isArray(corps)) {
            for (const k of Object.keys(corps)) if (k.startsWith('@')) delete corps[k];
        }
        return JSON.stringify(corps, null, 2);
    } catch {
        return texte;
    }
}

/** Déclenche le téléchargement d'un fichier texte (blob + lien éphémère, aucun onglet ouvert). */
export function telechargerFichier(nom, contenu, {doc = document, url = URL} = {}) {
    const blob = new Blob([contenu], {type: 'application/json'});
    const lien = doc.createElement('a');
    const adresse = url.createObjectURL(blob);
    lien.href = adresse;
    lien.download = nom;
    doc.body.appendChild(lien);
    lien.click();
    doc.body.removeChild(lien);
    url.revokeObjectURL(adresse);
}

/**
 * B-4 — exporter = récupérer le JSON puis le télécharger sous `nomFichierExport`. `lire(id)` résout le texte de la réponse ;
 * une erreur (409…) est relancée pour le toast du composant.
 */
export async function exporterScenario(lire, simulation, telecharger = telechargerFichier) {
    const nom = nomFichierExport(simulation);
    telecharger(nom, contenuExport(await lire(simulation.id)));
    return nom;
}

/** Erreur à afficher avant tout envoi : null si le fichier convient (.json, 5 Mo au plus). */
export function erreurFichierImport(file) {
    if (!file) return 'Choisissez un fichier .json.';
    if (!/\.json$/i.test(file.name || '')) return 'Le fichier doit être un export .json.';
    if (file.size > TAILLE_MAX_IMPORT) return 'Fichier trop volumineux (5 Mo maximum).';
    return null;
}

/** B-4 — corps multipart du POST /simulations/import : champ `file` (jamais de Content-Type forcé : frontière du navigateur). */
export function formDataImport(file) {
    const form = new FormData();
    form.append('file', file);
    return form;
}

/** Message d'une erreur d'import : première violation 422, sinon `detail` / `hydra:description`, sinon texte générique. */
export function messageErreurImport(e) {
    const data = e?.data ?? {};
    const violation = Array.isArray(data.violations) ? data.violations[0]?.message : null;
    return violation || data.detail || data['hydra:description'] || data.message || 'Import impossible.';
}

/**
 * B-4 — importer : `{cree}` ou, sur une 422/400, `{erreur}` affichée dans la modale (rien n'est créé). Toute autre erreur
 * (réseau, 403, 5xx) est relancée pour le toast.
 */
export async function importerScenario(importer, file) {
    const refus = erreurFichierImport(file);
    if (refus) return {erreur: refus};
    try {
        return {cree: await importer(file)};
    } catch (e) {
        if (e?.status === 422 || e?.status === 400) return {erreur: messageErreurImport(e)};
        throw e;
    }
}

/* ------------------------------------------------------------------ B-5 : badge « importé » */

const MARQUE_LIBELLE = / — importé de /;

/**
 * Informations d'import du scénario, ou null. Contrat api : drapeau `importe` (+ `importeDe`, `exporteLe`) sur le détail et la
 * liste ; replis pour une réponse plus ancienne : `parametres.importe`, puis la marque « — importé de » du libellé.
 */
export function infoImporte(simulation) {
    if (simulation?.importe === true) return {de: simulation.importeDe ?? null, exporteLe: simulation.exporteLe ?? null};
    if (simulation?.importe === false) return null;
    const i = simulation?.parametres?.importe;
    if (i && typeof i === 'object') return i;
    return MARQUE_LIBELLE.test(simulation?.libelle ?? '') ? {} : null;
}

export const estImporte = (simulation) => infoImporte(simulation) != null;

/** `2026-10-12T09:30:00+02:00` → `12/10/2026` (jour de l'export tel qu'écrit, sans conversion de fuseau). */
export function dateExport(exporteLe) {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(exporteLe || '');
    return m ? `${m[3]}/${m[2]}/${m[1]}` : null;
}

/** B-5 — « rejoue sur le snapshot du 12/10/2026 » ; sans date d'export : « rejoue sur le snapshot importé ». */
export function texteSnapshot(simulation) {
    const i = infoImporte(simulation);
    if (!i) return null;
    const d = dateExport(i.exporteLe);
    return d ? `rejoue sur le snapshot du ${d}` : 'rejoue sur le snapshot importé';
}

/** Identifiant à poster en `rejouerDe` : celui du scénario source s'il est importé, sinon null. */
export function rejouerDe(source) {
    return source && (source.importe === true || source.parametres?.importe) && source.id != null ? source.id : null;
}
